import { useEffect, useRef, useState } from "react";
import { emptyAddressBook, openAddressBook, type AddressBook } from "@timecapsule/crypto";
import { api } from "./api";
import { Icon } from "./Icon";
import { errorText, useT } from "./i18n";
import { navigate, usePath } from "./router";
import { isKeyRequestCancelled, useSession } from "./session";
import { initials, PersonMenu } from "./PersonMenu";
import { useRefreshHandler } from "./Refresh";
import { useShowMore } from "./pagedList";
import { ShowMore } from "./ShowMore";

export type MessageScope = { memberIds: number[]; label: string; generation: number };

/** Names and groups stay encrypted at rest and disappear immediately on Lock. */
export function useAddressBook() {
  const t = useT();
  const path = usePath();
  const { me, keyStatus, withKey, lockGeneration, lockStillValid } = useSession();
  const currentPubkey = useRef(me.pubkey);
  currentPubkey.current = me.pubkey;
  const [loaded, setLoaded] = useState<{ book: AddressBook; generation: number; pubkey: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const book = loaded?.generation === lockGeneration && loaded.pubkey === me.pubkey ? loaded.book : null;

  async function load() {
    const generation = lockGeneration;
    const pubkey = me.pubkey;
    setBusy(true);
    setError(null);
    try {
      const stored = await api.addressBook();
      const next = stored.blob ? await withKey(t.contacts.purposeOpen, async (key) => openAddressBook(stored.blob!, key)) : emptyAddressBook();
      if (lockStillValid(generation) && currentPubkey.current === pubkey) setLoaded({ book: next, generation, pubkey });
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { setLoaded(null); setError(null); }, [me.pubkey, lockGeneration]);
  // A held/plain key allows a quiet refresh after editing groups. A locked key asks only on click.
  useEffect(() => { if (keyStatus === "unlocked") void load(); }, [keyStatus, me.pubkey, path, lockGeneration]);
  useRefreshHandler(async () => { if (keyStatus === "unlocked") await load(); });
  return { book, busy, error, load };
}

export function PeoplePanel({ scope, onView, onClose }: { scope: MessageScope | null; onView: (scope: MessageScope) => void; onClose: () => void }) {
  const t = useT();
  const { reachable, followed, memberById, resolveMembers, keyStatus, lockGeneration } = useSession();
  const { book, busy, error, load } = useAddressBook();
  // A group may name members I no longer follow: their names come from a lookup (PLAN.md 4.20).
  useEffect(() => { if (book) resolveMembers({ ids: book.groups.flatMap((group) => group.members) }); }, [book, resolveMembers]);

  const people = reachable.filter((m) => followed.has(m.id));
  // Up to 500 followed members: the panel shows 50 at a time (PLAN.md 4.20).
  const peoplePage = useShowMore(people);
  const name = (id: number) => book?.aliases[String(id)] || memberById(id)?.display_name || `#${id}`;
  const view = (memberIds: number[], label: string) => onView({ memberIds, label, generation: lockGeneration });
  const write = (ids: number[]) => navigate("/compose", { recipientIds: ids });
  const personRow = (id: number) => {
    const member = memberById(id);
    return <li key={id} className="person-row">
      <PersonMenu name={name(id)} className={scope?.memberIds.length === 1 && scope.memberIds[0] === id ? "person-name active" : "person-name"} onView={() => view([id], name(id))} onWrite={() => write([id])} canWrite={!!member?.pubkey && followed.has(id)}>
        <span className="person-dot" aria-hidden="true">{initials(name(id))}</span><span>{name(id)}</span>
      </PersonMenu>
    </li>;
  };

  return <section className="people-panel" id="people-panel" aria-label={t.design.groupsAndPeople}>
    <div className="people-heading"><Icon name="people" size={17} /><span>{t.design.groupsAndPeople}</span><a href="/contacts" onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return; e.preventDefault(); navigate("/contacts"); }} title={t.design.editGroups} aria-label={t.design.editGroups}><Icon name="write" size={15} /></a><button className="icon-button" aria-label={t.design.hidePeople} title={t.design.hidePeople} onClick={onClose}><Icon name="close" size={16} /></button></div>
    <div className="people-tree">
      {!book && <button className="load-groups" disabled={busy || keyStatus === "none" || keyStatus === "mismatch"} onClick={() => void load()}><Icon name="lock" size={14} />{busy ? t.common.loading : t.design.openGroups}</button>}
      {book?.groups.map((group) => {
        const recipients = group.members.filter((id) => followed.has(id) && !!memberById(id)?.pubkey);
        const available = recipients.length === group.members.length;
        return <details className="people-group" key={group.id} open>
          <summary><Icon name="folder" size={16} /><span>{group.name}</span><small>{group.members.length}</small></summary>
          <div className="group-actions">
            <button disabled={group.members.length === 0} title={t.design.viewPublicMessages} onClick={() => view(group.members, group.name)}><Icon name="feed" size={13} />{t.design.viewMessages}</button>
            <button disabled={!available || recipients.length === 0 || recipients.length > 20} title={!available ? t.design.targetUnavailable : recipients.length > 20 ? t.compose.severalNote(20) : t.design.writeTo(group.name)} onClick={() => write(recipients)}><Icon name="write" size={13} />{t.design.message}</button>
          </div>
          <ul>{group.members.map(personRow)}</ul>
        </details>;
      })}
      {book && book.groups.length === 0 && <p className="small muted">{t.contacts.noGroups}</p>}
      <details className="people-group ungrouped" open><summary><Icon name="account" size={16} /><span>{t.contacts.people}</span><small>{people.length}</small></summary><ul>{peoplePage.shown.map((m) => personRow(m.id))}</ul><ShowMore hasMore={peoplePage.hasMore} onMore={peoplePage.showMore} /></details>
      {error && <p className="error small">{error}</p>}
    </div>
  </section>;
}
